import React, { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { tl, stat } from "@/lib/i18nFormat";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, LoadingBlock, EmptyState, StatusPill } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plug, KeyRound, Activity, Upload, Cog, RefreshCw, ShieldAlert, Copy, Play, Webhook, Download, Gauge } from "lucide-react";

import { useI18n } from "@/context/I18nContext";

const HEALTH_TONE: Record<string, string> = {
  ok: "good", active: "good", not_configured: "inactive", configured: "info", error: "critical",
};

const InactiveState: React.FC = () => {
  const {
    t
  } = useI18n();

  return (
    (<div className="flex items-start gap-3 rounded-lg border border-status-warn/30 bg-status-warn/8 p-4 text-status-warn" data-testid="connect-inactive">
      <ShieldAlert size={18} className="mt-0.5 shrink-0" />
      <div>
        <p className="font-medium">{t("Connect is turned off")}</p>
        <p className="text-sm opacity-90">{t(
          "Your tenant administrator has deactivated the integration module. Existing read-only configuration and history remain visible; new connectors, API keys, imports and jobs are disabled."
        )}</p>
      </div>
    </div>)
  );
};

const Connect: React.FC = () => {
  const {
    t
  } = useI18n();

  const { hasPerm } = useAuth();
  const isAdmin = hasPerm("connect.admin");
  const canView = hasPerm("connect.view");
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState<any>(null);
  const [connectors, setConnectors] = useState<any[]>([]);
  const [keys, setKeys] = useState<any[]>([]);
  const [traffic, setTraffic] = useState<any[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [imports, setImports] = useState<any[]>([]);
  const [jobs, setJobs] = useState<any[]>([]);
  const [webhooks, setWebhooks] = useState<any[]>([]);
  const [deliveries, setDeliveries] = useState<any[]>([]);
  const [exports, setExports] = useState<any[]>([]);
  const [monitoring, setMonitoring] = useState<any>(null);

  const load = useCallback(async () => {
    try {
      const [ov, cn, ak, tr, tpl, imp, jb, wh, dl, ex, mon] = await Promise.all([
        api.get("/connect/overview"),
        api.get("/connect/connectors"),
        api.get("/connect/api-keys"),
        api.get("/connect/traffic?limit=50"),
        api.get("/connect/imports/templates"),
        api.get("/connect/imports"),
        api.get("/connect/jobs"),
        api.get("/connect/webhooks"),
        api.get("/connect/webhooks/deliveries"),
        api.get("/connect/exports"),
        api.get("/connect/monitoring"),
      ]);
      setOverview(ov.data);
      setConnectors(cn.data.connectors || []);
      setKeys(ak.data.api_keys || []);
      setTraffic(tr.data.traffic || []);
      setTemplates(tpl.data.templates || []);
      setImports(imp.data.imports || []);
      setJobs(jb.data.jobs || []);
      setWebhooks(wh.data.webhooks || []);
      setDeliveries(dl.data.deliveries || []);
      setExports(ex.data.exports || []);
      setMonitoring(mon.data);
    } catch {
      toast.error("Could not load Connect data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (canView) load(); else setLoading(false); }, [load, canView]);

  if (!canView) return <Navigate to="/today" replace />;
  if (loading) return <LoadingBlock />;
  const moduleActive = overview?.module_active;

  return (
    (<div className="space-y-6" data-testid="connect-page">
      <PageHeader title="Integrations (Connect)" subtitle="Connectors, scoped API keys, traffic ingestion and imports"
        actions={<Button variant="outline" size="sm" onClick={load} data-testid="connect-refresh"><RefreshCw size={15} className="mr-1.5" />{t("Refresh")}</Button>} />
      {!moduleActive && <InactiveState />}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Card className="p-4" data-testid="stat-connectors"><p className="text-xs text-muted-foreground">{t("Connectors")}</p><p className="text-2xl font-semibold">{connectors.length}</p></Card>
        <Card className="p-4" data-testid="stat-keys"><p className="text-xs text-muted-foreground">{t("Active API keys")}</p><p className="text-2xl font-semibold">{overview?.active_api_keys ?? 0}</p></Card>
        <Card className="p-4" data-testid="stat-traffic"><p className="text-xs text-muted-foreground">{t("Traffic records")}</p><p className="text-2xl font-semibold">{overview?.traffic_records_in_scope ?? 0}</p></Card>
        <Card className="p-4" data-testid="stat-jobs"><p className="text-xs text-muted-foreground">{t("Ingestion jobs")}</p><p className="text-2xl font-semibold">{jobs.length}</p></Card>
      </div>
      <Tabs defaultValue="connectors">
        <TabsList data-testid="connect-tabs">
          <TabsTrigger value="connectors" data-testid="tab-connectors"><Plug size={15} className="mr-1.5" />{t("Connectors")}</TabsTrigger>
          <TabsTrigger value="keys" data-testid="tab-keys"><KeyRound size={15} className="mr-1.5" />{t("API Keys")}</TabsTrigger>
          <TabsTrigger value="traffic" data-testid="tab-traffic"><Activity size={15} className="mr-1.5" />{t("Traffic")}</TabsTrigger>
          <TabsTrigger value="imports" data-testid="tab-imports"><Upload size={15} className="mr-1.5" />{t("Imports")}</TabsTrigger>
          <TabsTrigger value="jobs" data-testid="tab-jobs"><Cog size={15} className="mr-1.5" />{t("Jobs")}</TabsTrigger>
          <TabsTrigger value="webhooks" data-testid="tab-webhooks"><Webhook size={15} className="mr-1.5" />{t("Webhooks")}</TabsTrigger>
          <TabsTrigger value="exports" data-testid="tab-exports"><Download size={15} className="mr-1.5" />{t("Exports")}</TabsTrigger>
          <TabsTrigger value="monitoring" data-testid="tab-monitoring"><Gauge size={15} className="mr-1.5" />{t("Monitoring")}</TabsTrigger>
        </TabsList>

        <TabsContent value="connectors" className="mt-4">
          <ConnectorsTab connectors={connectors} isAdmin={isAdmin && moduleActive} onChange={load} />
        </TabsContent>
        <TabsContent value="keys" className="mt-4">
          <KeysTab keys={keys} isAdmin={isAdmin && moduleActive} onChange={load} />
        </TabsContent>
        <TabsContent value="traffic" className="mt-4">
          <TrafficTab traffic={traffic} />
        </TabsContent>
        <TabsContent value="imports" className="mt-4">
          <ImportsTab templates={templates} imports={imports} isAdmin={isAdmin && moduleActive} onChange={load} />
        </TabsContent>
        <TabsContent value="jobs" className="mt-4">
          <JobsTab jobs={jobs} isAdmin={isAdmin && moduleActive} onChange={load} />
        </TabsContent>
        <TabsContent value="webhooks" className="mt-4">
          <WebhooksTab webhooks={webhooks} deliveries={deliveries} isAdmin={isAdmin && moduleActive} onChange={load} />
        </TabsContent>
        <TabsContent value="exports" className="mt-4">
          <ExportsTab exports={exports} isAdmin={isAdmin && moduleActive} moduleActive={moduleActive} onChange={load} />
        </TabsContent>
        <TabsContent value="monitoring" className="mt-4">
          <MonitoringTab monitoring={monitoring} />
        </TabsContent>
      </Tabs>
    </div>)
  );
};

const ConnectorsTab: React.FC<{ connectors: any[]; isAdmin: boolean; onChange: () => void }> = ({ connectors, isAdmin, onChange }) => {
  const {
    t
  } = useI18n();

  const [pollDate, setPollDate] = useState("2026-06-10");
  const testPoll = async (id: string) => {
    try {
      const r = await api.post(`/connect/connectors/${id}/test-poll?business_date=${pollDate}`);
      toast.success(tl("Mock poll ingested {ins} new / {upd} corrected records", { ins: r.data.result.inserted, upd: r.data.result.updated }));
      onChange();
    } catch (e: any) { toast.error(e?.response?.data?.detail || "Poll failed"); }
  };
  if (!connectors.length) return <EmptyState title="No connectors" description="No integration connectors have been configured." />;
  return (
    (<div className="space-y-3">
      {connectors.map((c) => (
        <Card key={c.id} className="p-4" data-testid={`connector-${c.key}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <p className="font-medium">{c.name}</p>
                <StatusPill status={HEALTH_TONE[c.health?.state] || "inactive"}>{c.health?.state || c.status}</StatusPill>
              </div>
              <p className="text-xs text-muted-foreground">{t("Provider:")}{" "}{c.provider}{" "}{t("· Kind:")}{" "}{c.kind}</p>
              {c.provider === "footfallcam_v9" && <p className="mt-1 text-xs text-status-warn">{t(
                "Live provider Not Configured — data shown is from the mock adapter + official-contract fixtures, never a live connection."
              )}</p>}
            </div>
            {isAdmin && c.provider === "footfallcam_v9" && (
              <div className="flex items-end gap-2">
                <div><Label className="text-xs">{t("Business date")}</Label><Input value={pollDate} onChange={(e) => setPollDate(e.target.value)} className="h-9 w-36" data-testid="poll-date" /></div>
                <Button size="sm" onClick={() => testPoll(c.id)} data-testid={`poll-${c.key}`}><Play size={14} className="mr-1.5" />{t("Run mock poll")}</Button>
              </div>
            )}
          </div>
        </Card>
      ))}
    </div>)
  );
};

const KeysTab: React.FC<{ keys: any[]; isAdmin: boolean; onChange: () => void }> = ({ keys, isAdmin, onChange }) => {
  const {
    t
  } = useI18n();

  const [name, setName] = useState("");
  const [scope, setScope] = useState("traffic.ingest");
  const [secret, setSecret] = useState<any>(null);
  const create = async () => {
    if (!name.trim()) return toast.error("Name required");
    try {
      const r = await api.post("/connect/api-keys", { name, scopes: [scope], rate_limit_per_min: 120 });
      setSecret(r.data.created);
      setName("");
      onChange();
    } catch (e: any) { toast.error(e?.response?.data?.detail || "Create failed"); }
  };
  const act = async (id: string, action: string) => {
    try { await api.post(`/connect/api-keys/${id}/${action}`); toast.success(tl("API key updated")); if (action === "rotate") { /* secret shown by response */ } onChange(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    (<div className="space-y-4">
      {isAdmin && (
        <Card className="p-4" data-testid="key-create">
          <p className="mb-3 text-sm font-medium">{t("Create scoped API key")}</p>
          <div className="flex flex-wrap items-end gap-2">
            <div><Label className="text-xs">{t("Name")}</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="h-9 w-52" data-testid="key-name" /></div>
            <div>
              <Label className="text-xs">{t("Scope")}</Label>
              <Select value={scope} onValueChange={setScope}>
                <SelectTrigger className="h-9 w-44" data-testid="key-scope"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="traffic.ingest">{t("traffic.ingest")}</SelectItem>
                  <SelectItem value="webhook.receive">{t("webhook.receive")}</SelectItem>
                  <SelectItem value="import.push">{t("import.push")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button size="sm" onClick={create} data-testid="key-create-btn">{t("Create key")}</Button>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">{t(
            "The API key and signing secret are shown once at creation and never again."
          )}</p>
        </Card>
      )}
      {!keys.length ? <EmptyState title="No API keys" description="No scoped API keys have been issued." /> : (
        <div className="space-y-2">
          {keys.map((k) => (
            <Card key={k.id} className="flex flex-wrap items-center justify-between gap-3 p-3" data-testid={`key-${k.id}`}>
              <div>
                <div className="flex items-center gap-2"><p className="font-medium">{k.name}</p><StatusPill status={k.status === "active" ? "good" : "critical"}>{stat(k.status)}</StatusPill></div>
                <p className="text-xs text-muted-foreground">{k.prefix}{t("··· · scopes:")}{" "}{(k.scopes || []).join(", ")}{" "}{t("· limit")}{" "}{k.rate_limit_per_min}/min</p>
              </div>
              {isAdmin && k.status === "active" && (
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => act(k.id, "revoke")} data-testid={`revoke-${k.id}`}>{t("Revoke")}</Button>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
      <Dialog open={!!secret} onOpenChange={(o) => !o && setSecret(null)}>
        <DialogContent data-testid="secret-dialog">
          <DialogHeader>
            <DialogTitle>{t("Save these secrets now")}</DialogTitle>
            <DialogDescription>{t("These values are shown once and cannot be retrieved again.")}</DialogDescription>
          </DialogHeader>
          {secret && (
            <div className="space-y-3 text-sm">
              <div>
                <Label className="text-xs">{t("API key")}</Label>
                <div className="flex items-center gap-2"><code className="flex-1 truncate rounded bg-muted px-2 py-1" data-testid="secret-api-key">{secret.api_key}</code>
                  <Button size="icon" variant="outline" onClick={() => { navigator.clipboard?.writeText(secret.api_key); toast.success("Copied"); }}><Copy size={14} /></Button></div>
              </div>
              <div>
                <Label className="text-xs">{t("Signing secret (HMAC)")}</Label>
                <div className="flex items-center gap-2"><code className="flex-1 truncate rounded bg-muted px-2 py-1" data-testid="secret-signing">{secret.signing_secret}</code>
                  <Button size="icon" variant="outline" onClick={() => { navigator.clipboard?.writeText(secret.signing_secret); toast.success("Copied"); }}><Copy size={14} /></Button></div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>)
  );
};

const TrafficTab: React.FC<{ traffic: any[] }> = ({ traffic }) => {
  const {
    t: tI18n
  } = useI18n();

  if (!traffic.length) return <EmptyState title="No traffic ingested" description="No normalized traffic records in your scope yet. Use a connector mock poll, a signed inbound push, or a scheduled file job." />;
  return (
    (<Card className="overflow-x-auto p-0">
      <table className="w-full text-sm" data-testid="traffic-table">
        <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
          <tr><th className="p-2">{tI18n("Provider")}</th><th className="p-2">{tI18n("Sensor")}</th><th className="p-2">{tI18n("Interval (local)")}</th><th className="p-2">{tI18n("Date")}</th><th className="p-2">{tI18n("In")}</th><th className="p-2">{tI18n("Out")}</th><th className="p-2">{tI18n("Basis")}</th><th className="p-2">{tI18n("Quality")}</th><th className="p-2">{tI18n("State")}</th></tr>
        </thead>
        <tbody>
          {traffic.map((t) => (
            <tr key={t.id} className="border-t border-border" data-testid={`traffic-row-${t.id}`}>
              <td className="p-2">{t.provider}</td>
              <td className="p-2">{t.sensor_id}</td>
              <td className="p-2 text-xs">{(t.interval_start_local || "").replace("T", " ").slice(0, 16)}</td>
              <td className="p-2">{t.business_date}</td>
              <td className="p-2 font-medium">{t.entrance_count}</td>
              <td className="p-2">{t.exit_count}</td>
              <td className="p-2 text-xs">{t.count_basis}</td>
              <td className="p-2 text-xs">{t.quality_state}</td>
              <td className="p-2 text-xs">{t.correction_state}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>)
  );
};

const ImportsTab: React.FC<{ templates: any[]; imports: any[]; isAdmin: boolean; onChange: () => void }> = ({ templates, imports, isAdmin, onChange }) => {
  const {
    t: tI18n
  } = useI18n();

  const [type, setType] = useState("performance");
  const [csv, setCsv] = useState("");
  const [preview, setPreview] = useState<any>(null);
  const tpl = templates.find((t) => t.type === type);

  const doPreview = async () => {
    try { const r = await api.post("/connect/imports/preview", { import_type: type, csv }); setPreview(r.data); }
    catch (e: any) { toast.error(e?.response?.data?.detail || "Preview failed"); }
  };
  const doCommit = async () => {
    try {
      const r = await api.post("/connect/imports/commit", { import_type: type, csv, idempotency_key: `imp-${Date.now()}` });
      toast.success(tl("Committed {n} rows", { n: r.data.rows })); setPreview(null); setCsv(""); onChange();
    } catch (e: any) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Commit failed"));
    }
  };
  const rollback = async (id: string) => {
    try { const r = await api.post(`/connect/imports/${id}/rollback`); toast.success(tl("Rolled back {n} records", { n: r.data.removed })); onChange(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || "Rollback failed"); }
  };

  return (
    (<div className="space-y-4">
      {isAdmin && (
        <Card className="p-4" data-testid="import-builder">
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <Label className="text-xs">{tI18n("Template")}</Label>
              <Select value={type} onValueChange={(v) => { setType(v); setPreview(null); }}>
                <SelectTrigger className="h-9 w-56" data-testid="import-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.type} value={t.type}>{t.label}{t.commit_enabled ? "" : tI18n("(preview only)")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {tpl && <p className="mt-2 text-xs text-muted-foreground">{tI18n("Columns:")}{" "}{tpl.headers.join(", ")}</p>}
          <Textarea value={csv} onChange={(e) => setCsv(e.target.value)} rows={5} className="mt-2 font-mono text-xs"
            placeholder={tpl ? tpl.headers.join(",") : ""} data-testid="import-csv" />
          <div className="mt-2 flex gap-2">
            <Button size="sm" variant="outline" onClick={doPreview} data-testid="import-preview-btn">{tI18n("Preview")}</Button>
            <Button size="sm" onClick={doCommit} disabled={!preview || preview.errors?.length || !tpl?.commit_enabled} data-testid="import-commit-btn">{tI18n("Commit")}</Button>
          </div>
          {preview && (
            <div className="mt-3 rounded border border-border p-3 text-sm" data-testid="import-preview">
              <p>{preview.valid}{" "}{tI18n("valid of")}{" "}{preview.total_rows}{" "}{tI18n("rows ·")}{" "}{preview.errors?.length || 0}{" "}{tI18n("errors")}</p>
              {preview.errors?.slice(0, 5).map((er: any, i: number) => <p key={i} className="text-xs text-status-critical">{tI18n("Row")}{" "}{er.row}: {er.error}</p>)}
            </div>
          )}
        </Card>
      )}
      {!imports.length ? <EmptyState title="No imports yet" description="Committed and rolled-back import runs appear here." /> : (
        <div className="space-y-2">
          {imports.map((j) => (
            <Card key={j.id} className="flex flex-wrap items-center justify-between gap-3 p-3" data-testid={`import-${j.id}`}>
              <div>
                <div className="flex items-center gap-2"><p className="font-medium">{j.import_type}</p><StatusPill status={j.status === "committed" ? "good" : j.status === "rolled_back" ? "inactive" : "critical"}>{stat(j.status)}</StatusPill></div>
                <p className="text-xs text-muted-foreground">{j.rows_valid}{" "}{tI18n("rows · v")}{j.version}</p>
              </div>
              {isAdmin && j.status === "committed" && j.reversible && (
                <Button size="sm" variant="outline" onClick={() => rollback(j.id)} data-testid={`rollback-${j.id}`}>{tI18n("Roll back")}</Button>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>)
  );
};

const JobsTab: React.FC<{ jobs: any[]; isAdmin: boolean; onChange: () => void }> = ({ jobs, isAdmin, onChange }) => {
  const {
    t
  } = useI18n();

  const tick = async () => {
    try { const r = await api.post("/connect/jobs/tick"); toast.success(tl("Processed {n} job(s)", { n: r.data.processed.length })); onChange(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || "Tick failed"); }
  };
  return (
    (<div className="space-y-3">
      {isAdmin && (
        <div className="flex justify-end">
          <Button size="sm" variant="outline" onClick={tick} data-testid="jobs-tick"><Play size={14} className="mr-1.5" />{t("Run worker tick")}</Button>
        </div>
      )}
      {!jobs.length ? <EmptyState title="No ingestion jobs" description="Unattended scheduled file-ingestion jobs appear here." /> : (
        <div className="space-y-2">
          {jobs.map((j) => (
            <Card key={j.id} className="flex flex-wrap items-center justify-between gap-3 p-3" data-testid={`job-${j.id}`}>
              <div>
                <div className="flex items-center gap-2"><p className="font-medium">{j.kind}</p><StatusPill status={j.status === "done" ? "good" : j.status === "dead_letter" ? "critical" : "info"}>{stat(j.status)}</StatusPill></div>
                <p className="text-xs text-muted-foreground">{j.provider}{" "}{t("· attempts")}{" "}{j.attempts || 0}{" "}{t("· trigger")}{" "}{j.trigger}</p>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>)
  );
};

const WebhooksTab: React.FC<{ webhooks: any[]; deliveries: any[]; isAdmin: boolean; onChange: () => void }> = ({ webhooks, deliveries, isAdmin, onChange }) => {
  const {
    t
  } = useI18n();

  const [url, setUrl] = useState("");
  const [evt, setEvt] = useState("traffic.ingested");
  const [secret, setSecret] = useState<any>(null);
  const create = async () => {
    if (!url.trim()) return toast.error("URL required");
    try {
      const r = await api.post("/connect/webhooks", { url, events: [evt] });
      setSecret(r.data.created); setUrl(""); onChange();
    } catch (e: any) { toast.error(e?.response?.data?.detail || "Register failed"); }
  };
  const test = async (id: string) => {
    try { await api.post(`/connect/webhooks/${id}/test`); await api.post("/connect/webhooks/tick"); toast.success("Test delivery sent"); onChange(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || "Test failed"); }
  };
  const del = async (id: string) => {
    try { await api.delete(`/connect/webhooks/${id}`); toast.success("Deleted"); onChange(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  return (
    (<div className="space-y-4">
      {isAdmin && (
        <Card className="p-4" data-testid="webhook-create">
          <p className="mb-3 text-sm font-medium">{t("Register outbound webhook")}</p>
          <div className="flex flex-wrap items-end gap-2">
            <div><Label className="text-xs">{t("Endpoint URL (public https)")}</Label><Input value={url} onChange={(e) => setUrl(e.target.value)} className="h-9 w-72" placeholder="https://example.com/hooks" data-testid="webhook-url" /></div>
            <div>
              <Label className="text-xs">{t("Event")}</Label>
              <Select value={evt} onValueChange={setEvt}>
                <SelectTrigger className="h-9 w-52" data-testid="webhook-event"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="traffic.ingested">{t("traffic.ingested")}</SelectItem>
                  <SelectItem value="import.committed">{t("import.committed")}</SelectItem>
                  <SelectItem value="reconciliation.case_opened">{t("reconciliation.case_opened")}</SelectItem>
                  <SelectItem value="test.ping">{t("test.ping")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button size="sm" onClick={create} data-testid="webhook-create-btn">{t("Register")}</Button>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">{t(
            "Deliveries are HMAC-signed; the signing secret is shown once. Private/loopback targets are blocked (SSRF-safe)."
          )}</p>
        </Card>
      )}
      {!webhooks.length ? <EmptyState title="No webhooks" description="No outbound webhook endpoints registered." /> : (
        <div className="space-y-2">
          {webhooks.map((w) => (
            <Card key={w.id} className="flex flex-wrap items-center justify-between gap-3 p-3" data-testid={`webhook-${w.id}`}>
              <div>
                <div className="flex items-center gap-2"><p className="truncate font-medium">{w.url}</p><StatusPill status={w.status === "active" ? "good" : "inactive"}>{stat(w.status)}</StatusPill></div>
                <p className="text-xs text-muted-foreground">{t("events:")}{" "}{(w.events || []).join(", ")}</p>
              </div>
              {isAdmin && (
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => test(w.id)} data-testid={`webhook-test-${w.id}`}>{t("Send test")}</Button>
                  <Button size="sm" variant="outline" onClick={() => del(w.id)} data-testid={`webhook-del-${w.id}`}>{t("Delete")}</Button>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
      {deliveries.length > 0 && (
        <Card className="overflow-x-auto p-0">
          <table className="w-full text-sm" data-testid="deliveries-table">
            <thead className="bg-muted/50 text-left text-xs text-muted-foreground"><tr><th className="p-2">{t("Event")}</th><th className="p-2">{t("Status")}</th><th className="p-2">{t("Code")}</th><th className="p-2">{t("Attempt")}</th></tr></thead>
            <tbody>
              {deliveries.slice(0, 20).map((d) => (
                <tr key={d.id} className="border-t border-border"><td className="p-2">{d.event_type}</td>
                  <td className="p-2"><StatusPill status={d.status === "delivered" ? "good" : "critical"}>{stat(d.status)}</StatusPill></td>
                  <td className="p-2">{d.response_code ?? "—"}</td><td className="p-2">{d.attempt}</td></tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
      <Dialog open={!!secret} onOpenChange={(o) => !o && setSecret(null)}>
        <DialogContent data-testid="webhook-secret-dialog">
          <DialogHeader><DialogTitle>{t("Save the signing secret now")}</DialogTitle><DialogDescription>{t("Shown once — used to verify HMAC signatures on your endpoint.")}</DialogDescription></DialogHeader>
          {secret && (
            <div className="flex items-center gap-2"><code className="flex-1 truncate rounded bg-muted px-2 py-1" data-testid="webhook-secret">{secret.signing_secret}</code>
              <Button size="icon" variant="outline" onClick={() => { navigator.clipboard?.writeText(secret.signing_secret); toast.success("Copied"); }}><Copy size={14} /></Button></div>
          )}
        </DialogContent>
      </Dialog>
    </div>)
  );
};

const ExportsTab: React.FC<{ exports: any[]; isAdmin: boolean; moduleActive: boolean; onChange: () => void }> = ({ exports, moduleActive, onChange }) => {
  const {
    t
  } = useI18n();

  const [ds, setDs] = useState("traffic");
  const request = async () => {
    try { await api.post("/connect/exports", { dataset: ds }); await api.post("/connect/exports/tick").catch(() => {}); toast.success("Export requested"); onChange(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || "Export failed"); }
  };
  const download = (id: string) => { window.open(`${(api.defaults.baseURL || "")}/connect/exports/${id}/download`, "_blank"); };
  return (
    (<div className="space-y-4">
      {moduleActive && (
        <Card className="p-4" data-testid="export-builder">
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <Label className="text-xs">{t("Dataset")}</Label>
              <Select value={ds} onValueChange={setDs}>
                <SelectTrigger className="h-9 w-52" data-testid="export-dataset"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="traffic">{t("Traffic")}</SelectItem><SelectItem value="performance">{t("Performance")}</SelectItem></SelectContent>
              </Select>
            </div>
            <Button size="sm" onClick={request} data-testid="export-request-btn">{t("Request export")}</Button>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">{t(
            "Exports are scope-limited, privacy-safe and bounded. They run asynchronously and become downloadable when done."
          )}</p>
        </Card>
      )}
      {!exports.length ? <EmptyState title="No exports" description="Requested data-portability exports appear here." /> : (
        <div className="space-y-2">
          {exports.map((e) => (
            <Card key={e.id} className="flex flex-wrap items-center justify-between gap-3 p-3" data-testid={`export-${e.id}`}>
              <div>
                <div className="flex items-center gap-2"><p className="font-medium">{e.dataset}</p><StatusPill status={e.status === "done" ? "good" : e.status === "failed" ? "critical" : "info"}>{stat(e.status)}</StatusPill></div>
                <p className="text-xs text-muted-foreground">{e.row_count ?? "—"}{" "}{t("rows")}</p>
              </div>
              {e.status === "done" && <Button size="sm" variant="outline" onClick={() => download(e.id)} data-testid={`export-dl-${e.id}`}><Download size={14} className="mr-1.5" />{t("Download")}</Button>}
            </Card>
          ))}
        </div>
      )}
    </div>)
  );
};

const MonitoringTab: React.FC<{ monitoring: any }> = ({ monitoring }) => {
  const {
    t
  } = useI18n();

  if (!monitoring) return <EmptyState title="No monitoring data" description="Integration monitoring is unavailable." />;
  const wd = monitoring.webhook_deliveries || {};
  return (
    (<div className="space-y-4" data-testid="monitoring">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Card className="p-4"><p className="text-xs text-muted-foreground">{t("Delivered")}</p><p className="text-2xl font-semibold text-status-good">{wd.delivered ?? 0}</p></Card>
        <Card className="p-4"><p className="text-xs text-muted-foreground">{t("Failed deliveries")}</p><p className="text-2xl font-semibold">{wd.failed ?? 0}</p></Card>
        <Card className="p-4"><p className="text-xs text-muted-foreground">{t("Dead-letter")}</p><p className="text-2xl font-semibold text-status-critical">{wd.dead_letter ?? 0}</p></Card>
        <Card className="p-4"><p className="text-xs text-muted-foreground">{t("Open integration exceptions")}</p><p className="text-2xl font-semibold">{monitoring.open_integration_exceptions ?? 0}</p></Card>
      </div>
      <Card className="p-4">
        <p className="mb-2 text-sm font-medium">{t("Connector health")}</p>
        {(monitoring.connectors || []).map((c: any) => (
          <div key={c.key} className="flex items-center justify-between border-t border-border py-2 first:border-0" data-testid={`mon-connector-${c.key}`}>
            <span className="text-sm">{c.key}</span>
            <StatusPill status={c.state === "ok" ? "good" : c.state === "error" ? "critical" : "inactive"}>{c.state || c.status}</StatusPill>
          </div>
        ))}
      </Card>
    </div>)
  );
};

export default Connect;
